import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Mail, MapPin, Search, LockKeyhole } from "lucide-react";
import { Link } from "react-router-dom";

const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

export default function Footer({ onOpenImpressum, onOpenDatenschutz }) {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      {/* CTA banner */}
      <div className="bg-gradient-to-r from-cyan-700 to-cyan-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Bereit für Ihre persönliche Pflegeberatung?
          </h2>
          <p className="text-cyan-100 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            Buchen Sie jetzt Ihren kostenlosen Video-Beratungstermin —
            schnell, einfach und von der Pflegekasse übernommen.
          </p>
          <Button
            data-testid="footer-cta-btn"
            onClick={() => scrollTo("#termin-buchen")}
            className="bg-white text-cyan-700 hover:bg-cyan-50 rounded-xl px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all group"
            size="lg"
          >
            Jetzt Termin buchen
            <ArrowRight className="h-5 w-5 ml-1 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>

      {/* Footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 shrink-0 overflow-hidden">
                <img
                  src={ICON_URL}
                  alt="Lehnen & Weinacht"
                  className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%] brightness-0 invert"
                />
              </div>
              <div>
                <span className="text-base font-bold text-white font-['Manrope'] leading-none block">Lehnen & Weinacht</span>
                <span className="text-[10px] font-semibold text-cyan-400 tracking-[0.1em] uppercase mt-0.5 block">Pflegeberatung & Alltagsbegleitung</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Professionelle Online-Pflegeberatung und Alltagsbegleitung in Nordrhein-Westfalen.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-['Manrope'] font-semibold text-sm text-slate-300 uppercase tracking-wider mb-4">
              Navigation
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Leistungen", href: "#leistungen" },
                { label: "So geht's", href: "#so-gehts" },
                { label: "Termin buchen", href: "#termin-buchen" },
                { label: "Über uns", href: "#ueber-uns" },
                { label: "FAQ", href: "#faq" },
              ].map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              <li>
                <Link to="/blog" className="text-sm text-slate-400 hover:text-white transition-colors">Ratgeber</Link>
              </li>
            </ul>
          </div>

          {/* Tools */}
          <div>
            <h4 className="font-['Manrope'] font-semibold text-sm text-slate-300 uppercase tracking-wider mb-4">
              Tools
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/blog/pflegeleistungen-finder" data-testid="footer-finder-link"
                  className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-white font-medium transition-colors">
                  <Search className="h-3.5 w-3.5" />
                  Leistungen prüfen
                </Link>
              </li>
              <li>
                <Link to="/blog/sozialraumbudget-rechner" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Sozialraumbudget-Rechner
                </Link>
              </li>
              <li>
                <Link to="/blog/verhinderungspflege-antrag" className="text-sm text-slate-400 hover:text-white transition-colors">
                  VP-Antragsassistent
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Manrope'] font-semibold text-sm text-slate-300 uppercase tracking-wider mb-4">
              Kontakt
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="tel:+491637917044" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                  <Phone className="h-4 w-4" /> 0163 791 7044
                </a>
              </li>
              <li>
                <a href="mailto:lehnenundweinacht@gmail.com" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                  <Mail className="h-4 w-4" /> lehnenundweinacht@gmail.com
                </a>
              </li>
              <li>
                <span className="flex items-center gap-2 text-sm text-slate-400">
                  <MapPin className="h-4 w-4" /> Hochstr. 72, 47929 Grefrath
                </span>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-['Manrope'] font-semibold text-sm text-slate-300 uppercase tracking-wider mb-4">
              Rechtliches
            </h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={onOpenImpressum}
                  data-testid="footer-impressum-btn"
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Impressum
                </button>
              </li>
              <li>
                <button
                  onClick={onOpenDatenschutz}
                  data-testid="footer-datenschutz-btn"
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Datenschutz
                </button>
              </li>
              <li>
                <button className="text-sm text-slate-400 hover:text-white transition-colors">
                  AGB
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Mitarbeiter-Portal */}
        <div className="mt-12 rounded-2xl border border-slate-700 bg-slate-800/60 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="footer-mitarbeiter-box">
          <div className="h-12 w-12 shrink-0 rounded-xl bg-cyan-700/20 text-cyan-300 flex items-center justify-center"><LockKeyhole className="h-6 w-6" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-['Manrope'] font-semibold text-white">Mitarbeiter-Portal</p>
            <p className="text-sm text-slate-400">Interner Bereich für Mitarbeitende von Lehnen & Weinacht – Kunden erfassen, Verfügbarkeit und Abwesenheiten pflegen.</p>
          </div>
          <Link to="/mitarbeiter/login" data-testid="footer-mitarbeiter-login"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-cyan-700 hover:bg-cyan-600 text-white font-semibold transition-colors shrink-0">
            Zum Mitarbeiter-Login <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-8 border-t border-slate-800 text-center">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    </footer>
  );
}
